import React, { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Alert, Badge, Button, Conductor, Stars01, Textarea } from "@opendate-app/setlist";

const GENERIC_ERROR = "Couldn't build that automation. Try rephrasing, or build it manually.";
const RATE_LIMIT_ERROR = "You're building automations quickly. Try again in a minute.";

const styles = {
  card: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    padding: "20px 24px 24px",
    borderRadius: 12,
    border: "1px solid var(--sl-border-brand)",
    background: "linear-gradient(180deg, var(--sl-bg-brand-primary), var(--sl-bg-primary))",
    boxShadow: "var(--sl-shadow-xs)",
  },
  head: { display: "flex", gap: 12, alignItems: "flex-start" },
  titleRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  title: { fontSize: 16, lineHeight: "24px", fontWeight: 600, color: "var(--sl-text-primary)" },
  subtitle: { fontSize: 14, lineHeight: "20px", color: "var(--sl-text-tertiary)" },
  footer: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" },
  chips: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 },
  tryLabel: { fontSize: 12, lineHeight: "18px", color: "var(--sl-text-quaternary)", marginRight: 4 },
  chip: { whiteSpace: "normal", textAlign: "left", maxWidth: "100%", borderRadius: 9999 },
};

const AiAutomationPrompt = ({ csrfToken, team, examples = [], placeholder, resolveVenueOwnershipId }) => {
  const [prompt, setPrompt] = useState("");
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState(null);
  const [built, setBuilt] = useState(null);

  const build = (text) => {
    const value = text.trim();
    if (!value || building) return;

    setBuilding(true);
    setError(null);
    setBuilt(null);
    resolveVenueOwnershipId((venueOwnershipId) => {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      axios.post(`/teams/${team.id}/custom_campaigns/ai_build`, { automation_description: value, venue_ownership_id: venueOwnershipId })
        .then(({ data }) => {
          setBuilt(data);
          setPrompt("");
        })
        .catch((err) => setError(err?.response?.status === 429 ? RATE_LIMIT_ERROR : err?.response?.data?.error || GENERIC_ERROR))
        .finally(() => setBuilding(false));
    }, () => setBuilding(false));
  };

  const pickExample = (example) => {
    setPrompt(example);
    build(example);
  };

  return (
    <section style={styles.card}>
      <div style={styles.head}>
        <Conductor width={36} height={36} animation={building ? "deep-sparkle-breath" : undefined} aria-hidden="true" />
        <div>
          <div style={styles.titleRow}>
            <span style={styles.title}>Describe the automation</span>
            <Badge color="brand" size="sm">Conductor</Badge>
          </div>
          <div style={styles.subtitle}>Write it in plain English. Conductor builds a draft you can review and edit before turning it on.</div>
        </div>
      </div>
      <Textarea
        aria-label="Describe the automation"
        rows={3}
        placeholder={placeholder}
        value={prompt}
        disabled={building}
        onChange={(e) => {
          setPrompt(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            build(prompt);
          }
        }}
      />
      <div style={styles.footer}>
        <div style={styles.chips}>
          {examples.length ? <span style={styles.tryLabel}>Try</span> : null}
          {examples.map((example) => (
            <Button key={example} variant="secondary" size="sm" style={styles.chip} disabled={building} onClick={() => pickExample(example)}>
              {example}
            </Button>
          ))}
        </div>
        <Button variant="primary" style={{ marginLeft: "auto" }} icon={building ? undefined : Stars01} disabled={building || !prompt.trim()} onClick={() => build(prompt)}>
          {building ? "Building…" : "Build draft"}
        </Button>
      </div>
      {error ? <Alert color="error" role="alert" title={error} /> : null}
      {built ? (
        <Alert
          color="success"
          title={`Draft "${built.name}" is ready. It won't run until you turn it on.`}
          description={[built.rationale, built.unmatched?.length ? `Left out: ${built.unmatched.join(", ")}.` : null].filter(Boolean).join(" ")}
          actions={<Button size="sm" variant="primary" onClick={() => { window.location.href = `/teams/${team.id}/custom_campaigns/${built.custom_campaign_id}`; }}>Review draft</Button>}
        />
      ) : null}
    </section>
  );
};

AiAutomationPrompt.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  examples: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  resolveVenueOwnershipId: PropTypes.func.isRequired,
};

export default AiAutomationPrompt;
